/**
 * 전사 처리 파이프라인 (TRANSCRIPT_PROTOCOL §3). 순수 함수: 같은 입력 → 같은 출력 (재실행·재개 안전).
 *   파싱 → 정렬 → 화자 역할 → 불일치 분류 → 용어 보정 → Working Transcript
 * 어느 한 기기 전사만 있어도 동작한다. 실패 대신 가능한 범위의 결과를 만든다.
 */
import { alignTranscripts } from './align';
import { detectTranscript, splitCombined } from './detect';
import { applyGlossary, classify, type DiscrepancyCategory } from './discrepancy';

/** 위험 관련 표현: 두 기기에서 같게 확인되지 않으면 항상 확인 대상 (TRANSCRIPT_PROTOCOL §6.3) */
const RISK_WORDS = /(죽|자살|자해|사라지|해치|때리|때렸|맞았|학대|목숨|극단|뛰어내|술을|수면제|폭력)/;
import { contentTokens, similarity } from './normalize';
import { parseGalaxy, parseIphone, type RawTurn } from './parse';
import { inferRoles, ROLE_MODEL_VERSION, scoreUnit, type InferredUnit } from './roles';

export type PipelineSegment = {
  segmentId: string;
  startMs: number;
  galaxyTimeMs?: number;
  iphoneTimeRange?: { startMs: number; endMs: number };
  sourceSpeakerLabel?: string;
  clinicalRole: 'counselor' | 'client' | 'unknown' | 'mixed';
  roleConfidence: number;
  counselorAct?: InferredUnit['counselorAct'];
  isBackchannel: boolean;
  localCorrection?: boolean;
  splitFrom?: { segmentId: string; charStart: number; charEnd: number };
  text: string;
  galaxyText?: string;
  iphoneText?: string;
  textConfidence: number;
  matchScore?: number;
  galaxyLines?: { lineStart: number; lineEnd: number; charStart: number; charEnd: number };
  iphoneLines?: { lineStart: number; lineEnd: number }[];
  discrepancyLevel?: 0 | 1 | 2 | 3 | 4;
  discrepancyCategory?: DiscrepancyCategory;
  verificationStatus: 'auto-matched' | 'needs-review' | 'unreviewed';
  clinicalPriority?: 'normal' | 'important' | 'critical';
  weakAgreement: boolean;
  glossaryCorrected?: string[];
};

export type PipelineResult = {
  segments: PipelineSegment[];
  stats: {
    galaxyTurns: number;
    iphoneBlocks: number;
    aligned: number;
    offsetMs: number | null;
    needsReview: number;
    critical: number;
    roleModelVersion: string;
    warnings: string[];
  };
};

const TEXT_CONF: Record<number, number> = { 0: 0.95, 1: 0.92, 2: 0.8, 3: 0.6, 4: 0.5 };

/** unit 텍스트에 해당하는 iPhone 부분 구간 찾기 (턴 구간 안에서) */
function subSpan(unitText: string, turnIphone: string): string {
  const u = contentTokens(unitText).map((t) => t.norm);
  const toks = contentTokens(turnIphone);
  if (!u.length || !toks.length) return '';
  const norms = toks.map((t) => t.norm);
  let best = { p: 0, len: u.length, s: -1 };
  for (let p = 0; p < norms.length; p++) {
    for (const d of [0, -1, 1, -2, 2]) {
      const len = u.length + d;
      if (len < 1 || p + len > norms.length) continue;
      const s = similarity(u, norms.slice(p, p + len));
      if (s > best.s) best = { p, len, s };
    }
  }
  const from = toks[best.p]!.start;
  const to = toks[Math.min(toks.length - 1, best.p + best.len - 1)]!.end;
  return turnIphone.slice(from, to);
}

export function runPipeline(input: { galaxyText?: string; iphoneText?: string; glossary?: string[] }): PipelineResult {
  let galaxyText = input.galaxyText ?? '';
  let iphoneText = input.iphoneText ?? '';
  // 한 칸에 두 기기 전사가 함께 들어온 경우 나눈다
  for (const t of [galaxyText, iphoneText]) {
    if (detectTranscript(t).kind === 'combined') {
      const s = splitCombined(t);
      if (s) {
        galaxyText = s.galaxy;
        iphoneText = s.iphone;
      }
    }
  }
  const glossary = input.glossary ?? [];
  const g = parseGalaxy(galaxyText);
  const i = parseIphone(iphoneText);
  const warnings = [...g.warnings.map((w) => `galaxy:${w}`), ...i.warnings.map((w) => `iphone:${w}`)];
  const segments: PipelineSegment[] = [];

  if (g.turns.length > 0) {
    const al = i.turns.length
      ? alignTranscripts(g.turns, i.turns)
      : { perTurn: g.turns.map(() => null), iphoneOnly: [], offsetMs: null };
    const units = inferRoles(g.turns);
    for (const u of units) {
      const turn = g.turns[u.turnIndex]!;
      const span = al.perTurn[u.turnIndex] ?? null;
      const iText = span ? subSpan(u.text, span.text) : '';
      const raw = span && iText ? classify(u.text, iText, glossary) : null;
      // 정렬 자체가 불확실하면(낮은 일치도) 위험 표현이 아닌 한 "중요 차이"로 단정하지 않는다
      const c = raw && raw.level === 3 && (span?.score ?? 0) < 0.7 ? { ...raw, level: 2 as const } : raw;
      const gl = iText ? applyGlossary(u.text, iText, glossary) : { text: u.text, corrected: [] };
      const level = c?.level;
      segments.push({
        segmentId: `g${u.turnIndex}-${u.unitIndex}`,
        startMs: turn.startMs,
        galaxyTimeMs: turn.startMs,
        ...(span ? { iphoneTimeRange: { startMs: span.startMs, endMs: span.endMs } } : {}),
        sourceSpeakerLabel: turn.label,
        clinicalRole: u.role,
        roleConfidence: u.confidence,
        ...(u.counselorAct ? { counselorAct: u.counselorAct } : {}),
        isBackchannel: u.isBackchannel,
        ...(u.localCorrection ? { localCorrection: true } : {}),
        ...(units.filter((x) => x.turnIndex === u.turnIndex).length > 1
          ? { splitFrom: { segmentId: `g${u.turnIndex}`, charStart: u.charStart, charEnd: u.charEnd } }
          : {}),
        text: gl.text,
        galaxyText: u.text,
        ...(iText ? { iphoneText: iText } : {}),
        textConfidence: level === undefined ? 0.75 : (TEXT_CONF[level] ?? 0.8),
        ...(span ? { matchScore: span.score } : {}),
        galaxyLines: { lineStart: turn.lineStart, lineEnd: turn.lineEnd, charStart: u.charStart, charEnd: u.charEnd },
        ...(span
          ? {
              iphoneLines: span.refs.map((r) => ({
                lineStart: i.turns[r.block]!.lineStart,
                lineEnd: i.turns[r.block]!.lineEnd,
              })),
            }
          : {}),
        ...(level !== undefined ? { discrepancyLevel: level, discrepancyCategory: c!.category } : {}),
        verificationStatus:
          !u.isBackchannel &&
          ((level !== undefined && level >= 3) || (RISK_WORDS.test(u.text) && (level === undefined || level >= 2)))
            ? 'needs-review'
            : level !== undefined && level <= 1
              ? 'auto-matched'
              : 'unreviewed',
        clinicalPriority:
          level === 4 || (RISK_WORDS.test(u.text) && level !== 0 && level !== 1)
            ? 'critical'
            : level === 3
              ? 'important'
              : 'normal',
        weakAgreement: u.weakAgreement,
        ...(gl.corrected.length ? { glossaryCorrected: gl.corrected } : {}),
      });
    }
    // Galaxy에 없는 iPhone 구간 (녹음 시작 전 등): 내용 특징만으로 역할 추정, 신뢰도 상한 0.7
    al.iphoneOnly.forEach((s, k) => {
      const sc = scoreUnit(s.text);
      const role = sc.score > 1 ? 'counselor' : sc.score < -1 ? 'client' : 'unknown';
      segments.push({
        segmentId: `i${k}`,
        startMs: Math.max(0, s.startMs - (al.offsetMs ?? 0)),
        iphoneTimeRange: { startMs: s.startMs, endMs: s.endMs },
        sourceSpeakerLabel: i.turns[s.refs[0]?.block ?? 0]?.label ?? '',
        clinicalRole: role,
        roleConfidence: role === 'unknown' ? 0.4 : 0.7,
        isBackchannel: false,
        text: s.text,
        iphoneText: s.text,
        textConfidence: 0.7,
        iphoneLines: s.refs.map((r) => ({
          lineStart: i.turns[r.block]!.lineStart,
          lineEnd: i.turns[r.block]!.lineEnd,
        })),
        verificationStatus: 'unreviewed',
        clinicalPriority: 'normal',
        weakAgreement: false,
      });
    });
    segments.sort((a, b) => a.startMs - b.startMs);
    return {
      segments,
      stats: {
        galaxyTurns: g.turns.length,
        iphoneBlocks: i.turns.length,
        aligned: al.perTurn.filter(Boolean).length,
        offsetMs: al.offsetMs,
        needsReview: segments.filter((s) => s.verificationStatus === 'needs-review').length,
        critical: segments.filter((s) => s.clinicalPriority === 'critical').length,
        roleModelVersion: ROLE_MODEL_VERSION,
        warnings,
      },
    };
  }

  // iPhone 전사만 있는 경우: 블록에 여러 화자가 섞이므로 역할 신뢰도를 낮게 둔다
  const asTurns: RawTurn[] = i.turns;
  const units = inferRoles(asTurns, { priorWeight: 0 });
  for (const u of units) {
    const turn = asTurns[u.turnIndex]!;
    const conf = Math.min(u.confidence, 0.7);
    segments.push({
      segmentId: `i${u.turnIndex}-${u.unitIndex}`,
      startMs: turn.startMs,
      iphoneTimeRange: { startMs: turn.startMs, endMs: asTurns[u.turnIndex + 1]?.startMs ?? turn.startMs },
      sourceSpeakerLabel: turn.label,
      clinicalRole: conf < 0.6 ? 'unknown' : u.role,
      roleConfidence: conf,
      isBackchannel: u.isBackchannel,
      text: u.text,
      iphoneText: u.text,
      textConfidence: 0.75,
      iphoneLines: [{ lineStart: turn.lineStart, lineEnd: turn.lineEnd }],
      verificationStatus: 'unreviewed',
      clinicalPriority: 'normal',
      weakAgreement: u.weakAgreement,
    });
  }
  return {
    segments,
    stats: {
      galaxyTurns: 0,
      iphoneBlocks: i.turns.length,
      aligned: 0,
      offsetMs: null,
      needsReview: 0,
      critical: 0,
      roleModelVersion: ROLE_MODEL_VERSION,
      warnings,
    },
  };
}
